import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { filterExpressComponents, filterExpressComponentsForDevice } from '@/utils/expressPayment';
import { trackExpressWalletResolved } from '@/utils/conversionTracking';
import type { PaymentMethodEnum } from '@/types/common';

type ExpressComponentLoadingState = {
  isLoading: boolean;
  isAvailable: boolean;
  availableMethods: PaymentMethodEnum[];
};
type ExpressComponentLoadingStates = Record<string, ExpressComponentLoadingState>;

const INITIAL_LOADING_STATE: ExpressComponentLoadingState = {
  isLoading: true,
  isAvailable: false,
  availableMethods: [],
};

export function useExpressComponents() {
  const context = useContext(CheckoutContext);
  const { paymentMethodsAvailable, template, featureFlags } = context;

  const expressComponents = useMemo(
    () => filterExpressComponentsForDevice(filterExpressComponents(paymentMethodsAvailable, featureFlags), template),
    [paymentMethodsAvailable, template.type, featureFlags]
  );
  // Anchor for `resolve_time_ms`: time from the express row mounting to each
  // wallet settling. Refs keep `handleComponentReady` identity-stable (it's
  // passed down to every wallet) while still reading fresh values.
  const mountTimeRef = useRef(Date.now());
  const contextRef = useRef(context);
  contextRef.current = context;
  const expressComponentsRef = useRef(expressComponents);
  expressComponentsRef.current = expressComponents;
  const [loadingStates, setLoadingStates] = useState<ExpressComponentLoadingStates>(() =>
    expressComponents.reduce<ExpressComponentLoadingStates>(
      (acc, config) => ({
        ...acc,
        [config.key]: INITIAL_LOADING_STATE,
      }),
      {}
    )
  );
  const { isAllComponentsLoaded, isComponentsAvailable, availableExpressMethods } = useMemo(
    () => ({
      isAllComponentsLoaded: Object.values(loadingStates).every((value) => !value.isLoading),
      isComponentsAvailable: Object.values(loadingStates).some((value) => value.isAvailable),
      availableExpressMethods: new Set(Object.values(loadingStates).flatMap((value) => value.availableMethods)),
    }),
    [loadingStates]
  );

  useEffect(() => {
    setLoadingStates((prev) =>
      expressComponents.reduce<ExpressComponentLoadingStates>(
        (acc, config) => ({
          ...acc,
          [config.key]: prev[config.key] ?? INITIAL_LOADING_STATE,
        }),
        {}
      )
    );
  }, [expressComponents]);

  const handleComponentReady = useCallback(
    (key: string, isAvailable: boolean, availableMethods?: PaymentMethodEnum[]) => {
      // Diagnostics live OUTSIDE the state updater so the updater stays pure
      // (React may invoke updaters twice under StrictMode/concurrent rendering).
      // First-settle semantics come from the tracker's own per-`express_key`
      // dedupe; the tracker is also internally guarded and can never throw.
      const config = expressComponentsRef.current.find((c) => c.key === key);
      trackExpressWalletResolved(contextRef.current, {
        express_key: key,
        wallet_provider: config?.providers.join('/') ?? 'unknown',
        wallet_state: isAvailable ? 'ready' : 'unavailable',
        resolve_time_ms: Math.max(0, Math.round(Date.now() - mountTimeRef.current)),
      });
      // A slot that doesn't name its wallets is assumed to have painted every
      // method it was configured for — true for every single-wallet slot.
      const reportedMethods = isAvailable ? (availableMethods ?? config?.paymentMethodNames ?? []) : [];
      setLoadingStates((prev) => ({
        ...prev,
        [key]: {
          isLoading: false,
          isAvailable,
          // Union, because one key can be reported by two sibling slots: the
          // Apple Pay new-tab fallback rendered alongside a Google-Pay-only
          // ECE both answer for `StripeExpressCheckout`.
          availableMethods: [...new Set([...(prev[key]?.availableMethods ?? []), ...reportedMethods])],
        },
      }));
    },
    []
  );

  return {
    isAllComponentsLoaded,
    isComponentsAvailable,
    availableExpressMethods,
    expressComponents,
    handleComponentReady,
  };
}
